import { useState, useEffect, useCallback } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { useTheme } from "@mui/styles";
import Loader from "../../../components/Loader/Loader";
import { fetchBrainFunctionAccess } from "../../../services/api";
import { formattedName } from "../../../utils/utils";
import { brainStyles } from "../../../theme/style";

export default function FunctionAccess() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const theme = useTheme();

  const classes = brainStyles(theme);

  const roleColors = {
    admin: {
      bgcolor: theme.palette.brain.blue.background,
      color: theme.palette.brain.blue.text,
      border: theme.palette.brain.blue.border,
    },
    reviewer: {
      bgcolor: theme.palette.brain.lightBlue.background,
      color: theme.palette.brain.lightBlue.text,
      border: theme.palette.brain.lightBlue.border,
    },
    approver: {
      bgcolor: theme.palette.brain.green.background,
      color: theme.palette.brain.green.text,
      border: theme.palette.brain.green.border,
    },
  };

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetchBrainFunctionAccess();
      if (response?.data?.status?.statusCode === "nam-200") {
        setUsers(response?.data?.data);
        setLoading(false);
      }
    } catch (error) {
      console.log("error-----> ", error);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 2,
        p: 3,
      }}
    >
      {/* Header Bar */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box>
          <Typography
            variant="h1"
            sx={{ fontSize: "1.25rem", color: theme.palette.secondary.main2 }}
          >
            Function Access Overview
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: theme.palette.primary.text2 }}
          >
            View reviewers, approvers and admins assigned to each function
          </Typography>
        </Box>
      </Box>

      {/* Table */}
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: "auto",
          border: `1px solid ${theme.palette.brain.mis.grey.border1}`,
          backgroundColor: theme.palette.brain.table.tableBackground,
          borderRadius: 2,
          "&::-webkit-scrollbar": { height: 6, width: 6 },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: theme.palette.brain.mis.grey.background1,
            borderRadius: 3,
          },
        }}
      >
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              position: "relative",
              width: "100%",
              height: "100%",
            }}
          >
            <Loader
              message={"Loading data..."}
              showLoadingImage={false}
              top={"50%"}
              circleStyle={{ color: theme.palette.secondary.main2 }}
            />
          </Box>
        ) : (
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell className={classes.tableHeader}>Name</TableCell>
                <TableCell className={classes.tableHeader}>Email</TableCell>
                <TableCell className={classes.tableHeader}>Function</TableCell>
                <TableCell className={classes.tableHeader}>Role</TableCell>
                <TableCell className={classes.tableHeader}>
                  Accessible Columns
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={5}
                    align="center"
                    sx={{ py: 4, color: theme.palette.brain.table.row.text }}
                  >
                    No users found
                  </TableCell>
                </TableRow>
              ) : (
                users.map((user, idx) => {
                  const rc = roleColors[user.role] || roleColors.reviewer;
                  return (
                    <TableRow
                      key={user.id}
                      hover
                      sx={{
                        backgroundColor:
                          idx % 2 === 0
                            ? "transparent"
                            : theme.palette.brain.table.row.background,

                        "&:hover": {
                          backgroundColor:
                            theme.palette.brain.table.row.hoverBackground,
                        },
                        transition: "background-color 0.12s ease",
                      }}
                    >
                      <TableCell
                        sx={{
                          color: theme.palette.brain.table.row.text,
                          fontSize: "0.78rem",
                        }}
                      >
                        {formattedName(user.name)}
                      </TableCell>
                      <TableCell
                        sx={{
                          color: theme.palette.brain.table.row.text,
                          fontSize: "0.78rem",
                        }}
                      >
                        {user.email}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={user.business_function}
                          size="small"
                          variant="outlined"
                          sx={{
                            textTransform: "capitalize",
                            fontSize: "0.75rem",
                            fontWeight: 500,
                            bgcolor: theme.palette.brain.blue.background,
                            color: theme.palette.brain.blue.text,
                            borderColor: theme.palette.brain.blue.border,
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={user.role}
                          size="small"
                          variant="outlined"
                          sx={{
                            textTransform: "capitalize",
                            fontSize: "0.75rem",
                            fontWeight: 500,
                            bgcolor: rc.bgcolor,
                            color: rc.color,
                            borderColor: rc.border,
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        {user.editableColumns &&
                        user.editableColumns.length > 0 ? (
                          <Box
                            sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}
                          >
                            {user.editableColumns.map((c) => (
                              <Chip
                                key={c}
                                label={c}
                                size="small"
                                sx={{
                                  bgcolor:
                                    theme.palette.brain.table.row
                                      .editedBackground,
                                  color: theme.palette.brain.yellow.text,
                                  fontWeight: 500,
                                  fontSize: "0.7rem",
                                  textTransform: "capitalize",
                                  padding: 0,
                                  margin: 0,
                                }}
                              />
                            ))}
                          </Box>
                        ) : (
                          <span
                            style={{
                              color: theme.palette.brain.table.row.text,
                              fontSize: "0.75rem",
                            }}
                          >
                            —
                          </span>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        )}
      </TableContainer>
    </Box>
  );
}
